//#region src/fingerprint.ts
function hash53(str, seed) {
	let h1 = 3735928559 ^ seed;
	let h2 = 1103547991 ^ seed;
	for (let i = 0; i < str.length; i++) {
		const ch = str.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
	h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
	return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
function errorFingerprint(error) {
	var _error$filename, _error$lineno;
	const key = `${error.type}_${error.message}_${(_error$filename = error.filename) !== null && _error$filename !== void 0 ? _error$filename : ""}_${(_error$lineno = error.lineno) !== null && _error$lineno !== void 0 ? _error$lineno : 0}`;
	return [0, 1].map((seed) => hash53(key, seed).toString(36).padStart(11, "0")).join("");
}
//#endregion
//#region src/mask.ts
const MASK = "***MASKED***";
const SENSITIVE_EXACT_KEYS = /* @__PURE__ */ new Set(["sk"]);
function isSensitiveKey(key) {
	const k = key.toLowerCase();
	return SENSITIVE_EXACT_KEYS.has(k) || k.includes("password") || k.includes("passwd") || k.includes("secret") || k.includes("token") || k.includes("credential") || k.includes("routing") && k.includes("key") || k.includes("integration") && k.includes("key") || k.includes("key") && k.includes("private") || k.includes("shared") && k.includes("key");
}
const SQL_PROPERTY = /(\\{0,7}["'])((?:\\(?!["'])|[^"'\\\n]){1,200})\1(\s*=\s*)(\\{0,7}["'])((?:\4\4|(?!\4)(?:\\+[^\\]|[^\\]))*)\4/g;
function maskSqlProperties(text) {
	return text.replace(SQL_PROPERTY, (whole, q, name, eq, vq) => isSensitiveKey(name) ? `${q}${name}${q}${eq}${vq}${MASK}${vq}` : whole);
}
function maskSensitiveFields(value, ancestors = /* @__PURE__ */ new WeakSet()) {
	if (typeof value === "string") return maskSqlProperties(value);
	if (value === null || typeof value !== "object") return value;
	if (ancestors.has(value)) return "[Circular]";
	ancestors.add(value);
	try {
		return maskObject(value, ancestors);
	} finally {
		ancestors.delete(value);
	}
}
function maskObject(value, ancestors) {
	if (Array.isArray(value)) return value.map((v) => maskSensitiveFields(v, ancestors));
	const obj = value;
	const pairNamesSecret = typeof obj.key === "string" && "value" in obj && isSensitiveKey(obj.key);
	const out = {};
	for (const [k, v] of Object.entries(obj)) {
		if (typeof v === "function") continue;
		if (isSensitiveKey(k) || pairNamesSecret && k === "value") out[k] = MASK;
		else out[k] = maskSensitiveFields(v, ancestors);
	}
	return out;
}
function sanitizeRequestData(data) {
	if (data === null || data === void 0) return data;
	if (typeof data === "object") return maskSensitiveFields(data);
	if (typeof data !== "string") return data;
	try {
		return maskSensitiveFields(JSON.parse(data));
	} catch (_unused) {
		return maskSqlProperties(data).replace(/"password"\s*:\s*"[^"]*"/gi, `"password":"${MASK}"`).replace(/password=([^&\s]*)/gi, `password=${MASK}`);
	}
}
const COUNT_WIDTH = 5;
function cleanField(value) {
	return (value !== null && value !== void 0 ? value : "").replace(/[\r\n\t]+/g, " ").replace(/\|/g, "¦").replace(/`/g, "'").replace(/\*/g, "·").trim();
}
function endpointOf(extra) {
	if (typeof (extra === null || extra === void 0 ? void 0 : extra.endpoint) === "string" && extra.endpoint) return cleanField(extra.endpoint);
	const url = typeof (extra === null || extra === void 0 ? void 0 : extra.url) === "string" ? extra.url : "";
	if (!url) return "-";
	let path = url.split("#")[0].split("?")[0];
	const absolute = path.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]*(\/.*)?$/i);
	if (absolute) path = (absolute[1] || "/").replace(/^\/api(?=\/|$)/, "") || "/";
	const method = typeof (extra === null || extra === void 0 ? void 0 : extra.method) === "string" ? extra.method.toUpperCase() : "";
	return cleanField(method ? `${method} ${path}` : path);
}
function textOf(error, endpoint) {
	const text = cleanField(error.message);
	const suffix = ` @ ${endpoint}`;
	return endpoint !== "-" && text.endsWith(suffix) ? text.slice(0, -suffix.length) : text;
}
function summaryLine(type, endpoint, count, text) {
	const t = cleanField(type);
	const required = `${t} | ${endpoint} | ×${count}`;
	if (required.length > 120) return `${t} | <too-long> | ×${count}`;
	const room = 120 - `${t} | ${endpoint} | ×`.length - COUNT_WIDTH - 3;
	if (!text || room < 2) return required;
	return `${required} | ${text.length <= room ? text : `${text.slice(0, room - 1)}…`}`;
}
function compareGroups(a, b) {
	const aHasEndpoint = a.endpoint !== "-" ? 0 : 1;
	const bHasEndpoint = b.endpoint !== "-" ? 0 : 1;
	if (aHasEndpoint !== bHasEndpoint) return aHasEndpoint - bHasEndpoint;
	if (a.count !== b.count) return b.count - a.count;
	const ak = `${a.type}\u0000${a.endpoint}\u0000${a.text}`;
	const bk = `${b.type}\u0000${b.endpoint}\u0000${b.text}`;
	if (ak < bk) return -1;
	if (ak > bk) return 1;
	return 0;
}
function buildErrorsSummary(errors, budget = 900) {
	const groups = /* @__PURE__ */ new Map();
	for (const error of errors) {
		const endpoint = endpointOf(error.extra);
		const text = textOf(error, endpoint);
		const key = `${error.type}\u0000${endpoint}\u0000${text}`;
		const group = groups.get(key);
		if (group) group.count += 1;
		else groups.set(key, {
			type: error.type,
			endpoint,
			text,
			count: 1
		});
	}
	const sorted = [...groups.values()].sort(compareGroups);
	const lines = [];
	let used = 0;
	for (let i = 0; i < sorted.length; i++) {
		const g = sorted[i];
		const line = summaryLine(g.type, g.endpoint, g.count, g.text);
		const leftAfter = sorted.length - i - 1;
		const reserve = leftAfter > 0 ? `+${leftAfter} more`.length + 1 : 0;
		if (used + line.length + (lines.length ? 1 : 0) + reserve > budget) {
			lines.push(`+${sorted.length - i} more`);
			break;
		}
		used += line.length + (lines.length ? 1 : 0);
		lines.push(line);
	}
	return lines.join("\n");
}
//#endregion
//#region src/report.ts
function pageOf(url) {
	if (!url) return "-";
	try {
		const { origin, pathname } = new URL(url);
		return `${origin}${pathname}`.slice(0, 200);
	} catch (_unused) {
		return url.split(/[?#]/)[0].slice(0, 200);
	}
}
const label = (type) => type.replace(/_/g, " ").toUpperCase();
function formatReport(errors, metadata) {
	const first = errors[0];
	let markdown = `**Frontend Error Batch Report**\n\n`;
	markdown += `**Account ID:** ${(first === null || first === void 0 ? void 0 : first.accountId) || "-"}\n`;
	markdown += `**Report Time:** ${new Date(metadata.reportTime).toISOString()}\n`;
	markdown += `**Error ID:** ${(first === null || first === void 0 ? void 0 : first.errorId) || "-"}\n`;
	markdown += `**Page:** ${pageOf(first === null || first === void 0 ? void 0 : first.url)}\n`;
	markdown += `**Errors:**\n`;
	markdown += `${buildErrorsSummary(errors, 900 - markdown.length)}\n\n`;
	if (first) {
		markdown += `**Common Environment Information:**\n`;
		markdown += `**User Agent:** ${first.userAgent || "Unknown"}\n`;
		markdown += `**Viewport:** ${first.viewport || "Unknown"}\n`;
		markdown += `**Screen:** ${first.screen || "Unknown"}\n`;
		markdown += `**Language:** ${first.language || "Unknown"}\n`;
		markdown += `**Environment:** ${first.environment || "Unknown"}\n`;
		if (first.version && first.version !== "unknown") markdown += `**App Version:** ${first.version}\n`;
		if (first.buildTime && first.buildTime !== "unknown") markdown += `**Build Time:** ${first.buildTime}\n`;
		markdown += `**Session ID:** ${first.sessionId}\n`;
		if (first.userId) markdown += `**User ID:** ${first.userId}\n`;
		for (const [name, value] of Object.entries(first.fields)) if (value) markdown += `**${name}:** ${value}\n`;
		markdown += `\n`;
	}
	const types = [...new Set(errors.map((e) => e.type))];
	markdown += `**Total Errors:** ${metadata.errorCount}\n`;
	markdown += `**Error Types:** ${types.join(", ")}\n\n`;
	const counts = {};
	for (const e of errors) counts[e.type] = (counts[e.type] || 0) + 1;
	markdown += `**Error Summary:**\n\n`;
	for (const [type, count] of Object.entries(counts)) markdown += `**${label(type)}:** ${count} (${(count / errors.length * 100).toFixed(1)}%)\n`;
	markdown += `\n`;
	markdown += `**Error Details:**\n\n`;
	errors.slice(0, 5).forEach((error, index) => {
		markdown += `**Error ${index + 1}: ${label(error.type)}**\n`;
		markdown += `**Message:** ${error.message}\n`;
		markdown += `**Time:** ${new Date(error.timestamp).toISOString()}\n`;
		markdown += `**URL:** ${error.url}\n`;
		if (error.isDuplicate) markdown += `**Occurrences:** ${error.duplicateCount}\n`;
		if (error.filename) markdown += `**File:** ${error.filename}:${error.lineno || 0}\n`;
		if (error.stack) markdown += `**Stack:** ${error.stack.split("\n")[0]}\n`;
		markdown += `**Error ID:** ${error.errorId}\n\n`;
	});
	if (errors.length > 5) markdown += `... and ${errors.length - 5} more errors\n\n`;
	if (metadata.fingerprints.length > 0) {
		markdown += `**Error Patterns:**\n\n`;
		for (const fp of metadata.fingerprints.slice(0, 5)) {
			const duration = fp.lastOccurrence - fp.firstOccurrence;
			const durationText = duration > 0 ? ` (${Math.round(duration / 1e3)}s duration)` : "";
			markdown += `**${fp.fingerprint}:** ${fp.count} occurrences${durationText}\n`;
		}
		if (metadata.fingerprints.length > 5) markdown += `... and ${metadata.fingerprints.length - 5} more patterns\n`;
		markdown += `\n`;
	}
	const details = errors.map((error) => {
		const { userAgent: _userAgent, viewport: _viewport, screen: _screen, language: _language, environment: _environment, version: _version, buildTime: _buildTime, sessionId: _sessionId, userId: _userId, accountId: _accountId, fields: _fields, extra, ...rest } = error;
		return {
			...rest,
			...extra === void 0 ? {} : { extra: maskSensitiveFields(extra) }
		};
	});
	markdown += `**Complete Batch Data:**\n`;
	markdown += `\`\`\`json\n${JSON.stringify({
		errors: details,
		metadata
	}, null, 2)}\n\`\`\`\n`;
	return markdown;
}
//#endregion
//#region src/reporter.ts
const IGNORED_ERROR_PATTERNS = [
	/^\[Vue warn\]/i,
	/^\[Vue tip\]/i,
	/ResizeObserver loop/i,
	/Download the Vue Devtools/i
];
const MAX_QUEUE_SIZE = 100;
const REPORT_INTERVAL_MS = 3e4;
const FINGERPRINT_CLEANUP_INTERVAL_MS = 36e5;
const MAX_FINGERPRINT_AGE_MS = 6e6;
const MAX_SAME_ERROR_REPORTS = 10;
const MAX_REPORTED_FINGERPRINTS = 20;
const SESSION_STORAGE_KEY = "frontend_alert_session_id";
const DUPLICATE_REPORT_THRESHOLDS = [
	5,
	10,
	25,
	50,
	100,
	250,
	500,
	1e3,
	2500,
	5e3
];
const DUPLICATE_REPORT_GAP_MS = 3e5;
const REJECTION_MAX_CHARS = 500;
const MAX_ATTEMPTS = 3;
const RETRYABLE_STATUSES = /* @__PURE__ */ new Set([408, 429]);
const REQUEST_TIMEOUT_MS = 3e4;
function uuid() {
	const c = globalThis.crypto;
	if (typeof c.randomUUID === "function") return c.randomUUID();
	const bytes = c.getRandomValues(/* @__PURE__ */ new Uint8Array(16));
	bytes[6] = bytes[6] & 15 | 64;
	bytes[8] = bytes[8] & 63 | 128;
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function isElement(target) {
	return typeof (target === null || target === void 0 ? void 0 : target.tagName) === "string";
}
function requestTarget(request) {
	var _request$method;
	if (!(request === null || request === void 0 ? void 0 : request.url)) return "";
	let path = request.url.split(/[?#]/)[0];
	const absolute = /^[a-z][a-z0-9+.-]*:\/\/[^/]*(\/.*)?$/i.exec(path);
	if (absolute) path = (absolute[1] || "/").replace(/^\/api(?=\/|$)/, "") || "/";
	return `${((_request$method = request.method) !== null && _request$method !== void 0 ? _request$method : "").toUpperCase()} ${path}`.trim();
}
function maskedJson(value) {
	const json = JSON.stringify(maskSensitiveFields(value), (_key, v) => typeof v === "bigint" ? v.toString() : v);
	return JSON.parse(json);
}
function plainExtra(extra) {
	try {
		return maskedJson(extra);
	} catch (_unused) {
		return { unserializable: true };
	}
}
function rejectionMessage(reason) {
	if (typeof reason === "string") return reason;
	if (reason && typeof reason === "object") {
		const message = reason.message;
		if (typeof message === "string") return message;
		try {
			const plain = maskedJson(reason);
			if (plain !== null && typeof plain === "object" && Object.keys(plain).length > 0) return JSON.stringify(plain).slice(0, REJECTION_MAX_CHARS);
		} catch (_unused2) {}
	}
	return "Unhandled promise rejection";
}
function createReporter(options) {
	var _options$ignorePatter, _options$fetch;
	const ignored = [...IGNORED_ERROR_PATTERNS, ...(_options$ignorePatter = options.ignorePatterns) !== null && _options$ignorePatter !== void 0 ? _options$ignorePatter : []];
	const send = (_options$fetch = options.fetch) !== null && _options$fetch !== void 0 ? _options$fetch : ((input, init) => fetch(input, init));
	let queue = [];
	let queuedCredentials = /* @__PURE__ */ new WeakMap();
	let attempts = /* @__PURE__ */ new WeakMap();
	const fingerprints = /* @__PURE__ */ new Map();
	let started = false;
	let generation = 0;
	let reportTimer = null;
	let cleanupTimer = null;
	let nativeConsoleError = null;
	const isIgnored = (message) => ignored.some((re) => re.test(message));
	const currentUrl = () => {
		var _options$pageUrl, _options$pageUrl2;
		return (_options$pageUrl = (_options$pageUrl2 = options.pageUrl) === null || _options$pageUrl2 === void 0 ? void 0 : _options$pageUrl2.call(options)) !== null && _options$pageUrl !== void 0 ? _options$pageUrl : window.location.href;
	};
	function sessionId() {
		try {
			let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
			if (!id) {
				id = uuid();
				sessionStorage.setItem(SESSION_STORAGE_KEY, id);
			}
			return id;
		} catch (_unused3) {
			return "unknown";
		}
	}
	function context() {
		try {
			return options.context();
		} catch (_unused4) {
			return { accountId: "" };
		}
	}
	function credentials() {
		try {
			return options.credentials();
		} catch (_unused5) {
			return null;
		}
	}
	function capture(error, duplicate) {
		var _error$timestamp, _error$url, _error$userAgent, _ctx$userId, _ctx$userRole, _ctx$fields, _options$version, _options$buildTime;
		const ctx = context();
		const now = Date.now();
		return {
			...error,
			message: maskSqlProperties(error.message),
			...error.stack === void 0 ? {} : { stack: maskSqlProperties(error.stack) },
			...error.extra === void 0 ? {} : { extra: plainExtra(error.extra) },
			timestamp: (_error$timestamp = error.timestamp) !== null && _error$timestamp !== void 0 ? _error$timestamp : now,
			url: (_error$url = error.url) !== null && _error$url !== void 0 ? _error$url : currentUrl(),
			userAgent: (_error$userAgent = error.userAgent) !== null && _error$userAgent !== void 0 ? _error$userAgent : navigator.userAgent,
			...duplicate,
			...duplicate.firstOccurrence === void 0 ? {} : { timeSinceFirst: now - duplicate.firstOccurrence },
			userId: (_ctx$userId = ctx.userId) !== null && _ctx$userId !== void 0 ? _ctx$userId : "",
			accountId: ctx.accountId,
			userRole: (_ctx$userRole = ctx.userRole) !== null && _ctx$userRole !== void 0 ? _ctx$userRole : "",
			fields: (_ctx$fields = ctx.fields) !== null && _ctx$fields !== void 0 ? _ctx$fields : {},
			environment: options.environment,
			version: (_options$version = options.version) !== null && _options$version !== void 0 ? _options$version : "unknown",
			buildTime: (_options$buildTime = options.buildTime) !== null && _options$buildTime !== void 0 ? _options$buildTime : "unknown",
			viewport: `${window.innerWidth}x${window.innerHeight}`,
			screen: `${window.screen.width}x${window.screen.height}`,
			language: navigator.language,
			referrer: document.referrer,
			title: document.title,
			sessionId: sessionId(),
			errorId: uuid()
		};
	}
	function shouldReportAgain(record) {
		if (Date.now() - (record.lastReportTime || record.firstOccurrence) > DUPLICATE_REPORT_GAP_MS) return true;
		return DUPLICATE_REPORT_THRESHOLDS.includes(record.count) && record.reportCount < MAX_SAME_ERROR_REPORTS;
	}
	function enqueue(error) {
		queuedCredentials.set(error, credentials());
		queue.push(error);
		if (queue.length >= MAX_QUEUE_SIZE) flush();
	}
	function reportError(error) {
		try {
			const fingerprint = errorFingerprint(error);
			const now = Date.now();
			const record = fingerprints.get(fingerprint);
			if (record) {
				record.count++;
				record.lastOccurrence = now;
				if (!shouldReportAgain(record)) return;
				record.lastReportTime = now;
				record.reportCount++;
				enqueue(capture(error, {
					isDuplicate: true,
					duplicateCount: record.count,
					firstOccurrence: record.firstOccurrence
				}));
				return;
			}
			fingerprints.set(fingerprint, {
				fingerprint,
				count: 1,
				firstOccurrence: now,
				lastOccurrence: now,
				lastReportTime: now,
				reportCount: 1
			});
			enqueue(capture(error, {
				isDuplicate: false,
				duplicateCount: 1
			}));
		} catch (error_) {
			nativeConsoleError === null || nativeConsoleError === void 0 || nativeConsoleError("[ErrorReporter] Failed to process error:", error_);
		}
	}
	function recentFingerprints() {
		return Array.from(fingerprints.values()).sort((a, b) => b.lastOccurrence - a.lastOccurrence).slice(0, MAX_REPORTED_FINGERPRINTS).map(({ fingerprint, count, firstOccurrence, lastOccurrence }) => ({
			fingerprint,
			count,
			firstOccurrence,
			lastOccurrence
		}));
	}
	function reportsOf(errors) {
		const reports = [];
		for (const error of errors) {
			var _queuedCredentials$ge, _last$credentials, _last$credentials2;
			const queuedWith = (_queuedCredentials$ge = queuedCredentials.get(error)) !== null && _queuedCredentials$ge !== void 0 ? _queuedCredentials$ge : null;
			const last = reports.length > 0 ? reports[reports.length - 1] : void 0;
			if (last && last.errors[0].accountId === error.accountId && ((_last$credentials = last.credentials) === null || _last$credentials === void 0 ? void 0 : _last$credentials.token) === (queuedWith === null || queuedWith === void 0 ? void 0 : queuedWith.token) && ((_last$credentials2 = last.credentials) === null || _last$credentials2 === void 0 ? void 0 : _last$credentials2.ubid) === (queuedWith === null || queuedWith === void 0 ? void 0 : queuedWith.ubid)) last.errors.push(error);
			else reports.push({
				credentials: queuedWith,
				errors: [error]
			});
		}
		return reports;
	}
	function signerOf(report) {
		if (report.credentials) return report.credentials;
		const now = credentials();
		if (!now) return "wait";
		const account = report.errors[0].accountId;
		return !account || account === now.accountId ? now : "never";
	}
	async function post(errors, signAs, unload) {
		const metadata = {
			reportTime: Date.now(),
			errorCount: errors.length,
			...unload ? { sentOnUnload: true } : {},
			fingerprints: recentFingerprints()
		};
		let body;
		try {
			let message = formatReport(errors, metadata);
			if (unload) message += `\n\n**Sent via fetch keepalive (page unload)**`;
			body = JSON.stringify({ message });
		} catch (_unused6) {
			return "drop";
		}
		let signature = {};
		try {
			signature = options.sign(body, signAs);
		} catch (_unused7) {}
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
		try {
			const response = await send(new URL(options.logUrl(), window.location.href).toString(), {
				method: "POST",
				credentials: "include",
				keepalive: unload,
				headers: {
					"Content-Type": "application/json",
					...signature
				},
				body,
				signal: controller.signal
			});
			if (response.ok) return "sent";
			return response.status >= 500 || RETRYABLE_STATUSES.has(response.status) ? "retry" : "drop";
		} catch (_unused8) {
			return "retry";
		} finally {
			clearTimeout(timeout);
		}
	}
	async function flush() {
		if (queue.length === 0) return;
		const errors = queue;
		queue = [];
		const run = generation;
		const putBack = (back) => {
			if (run === generation && queue.length < MAX_QUEUE_SIZE) queue.unshift(...back.slice(0, MAX_QUEUE_SIZE - queue.length));
		};
		await Promise.all(reportsOf(errors).map(async (report) => {
			const signer = signerOf(report);
			if (signer === "never") return;
			if (signer === "wait") {
				putBack(report.errors);
				return;
			}
			if (await post(report.errors, signer, false) !== "retry") return;
			putBack(report.errors.filter((error) => {
				var _attempts$get;
				const tries = ((_attempts$get = attempts.get(error)) !== null && _attempts$get !== void 0 ? _attempts$get : 0) + 1;
				attempts.set(error, tries);
				return tries < MAX_ATTEMPTS;
			}));
		}));
	}
	function flushOnUnload() {
		const errors = queue;
		queue = [];
		for (const report of reportsOf(errors)) {
			const signer = signerOf(report);
			if (typeof signer === "object") post(report.errors, signer, true);
			else if (signer === "wait") queue.push(...report.errors);
		}
	}
	function cleanupFingerprints() {
		const now = Date.now();
		for (const [fingerprint, record] of fingerprints) if (now - record.firstOccurrence > MAX_FINGERPRINT_AGE_MS) fingerprints.delete(fingerprint);
	}
	function handleError(event) {
		var _event$message, _event$error;
		if (isElement(event.target)) return;
		if (isIgnored((_event$message = event.message) !== null && _event$message !== void 0 ? _event$message : "")) return;
		reportError({
			type: "javascript_error",
			message: event.message || "Unknown error",
			filename: event.filename || "Unknown file",
			lineno: event.lineno || 0,
			colno: event.colno || 0,
			stack: ((_event$error = event.error) === null || _event$error === void 0 ? void 0 : _event$error.stack) || "No stack trace"
		});
	}
	function handleUnhandledRejection(event) {
		const reason = event.reason;
		if (reason && typeof reason === "object") {
			const tagged = reason;
			if (tagged.manuallyBlocked) {
				event.preventDefault();
				return;
			}
			if (tagged.reportedByInterceptor) return;
		}
		const stack = reason === null || reason === void 0 ? void 0 : reason.stack;
		reportError({
			type: "promise_rejection",
			message: rejectionMessage(reason),
			stack: typeof stack === "string" ? stack : "No stack trace"
		});
	}
	function handleResourceError(event) {
		const target = event.target;
		if (!isElement(target)) return;
		const url = target.src || target.href || "";
		reportError({
			type: "resource_error",
			message: `Failed to load resource: ${url || "Unknown resource"}`,
			resourceType: target.tagName.toLowerCase(),
			resourceUrl: url
		});
	}
	function handleBeforeUnload() {
		if (queue.length > 0) flushOnUnload();
	}
	function captureConsoleError(args) {
		const error = args.find((a) => a instanceof Error);
		const text = (a) => {
			var _JSON$stringify;
			if (typeof a === "string") return a;
			if (a !== null && typeof a === "object") return (_JSON$stringify = JSON.stringify(maskedJson(a))) !== null && _JSON$stringify !== void 0 ? _JSON$stringify : "null";
			return String(a);
		};
		const message = error ? error.message : args.map(text).join(" ");
		if (!message || isIgnored(message)) return;
		reportError({
			type: "console_error",
			message,
			stack: (error === null || error === void 0 ? void 0 : error.stack) || "No stack trace"
		});
	}
	return {
		start: () => {
			if (started) return;
			started = true;
			window.addEventListener("error", handleError, true);
			window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
			document.addEventListener("error", handleResourceError, true);
			window.addEventListener("beforeunload", handleBeforeUnload);
			const native = console.error;
			nativeConsoleError = native;
			console.error = (...args) => {
				native.apply(console, args);
				try {
					captureConsoleError(args);
				} catch (_unused9) {}
			};
			reportTimer = setInterval(() => {
				if (queue.length > 0) flush();
			}, REPORT_INTERVAL_MS);
			cleanupTimer = setInterval(cleanupFingerprints, FINGERPRINT_CLEANUP_INTERVAL_MS);
		},
		stop: () => {
			if (reportTimer) clearInterval(reportTimer);
			if (cleanupTimer) clearInterval(cleanupTimer);
			reportTimer = null;
			cleanupTimer = null;
			window.removeEventListener("error", handleError, true);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
			document.removeEventListener("error", handleResourceError, true);
			window.removeEventListener("beforeunload", handleBeforeUnload);
			if (nativeConsoleError) {
				console.error = nativeConsoleError;
				nativeConsoleError = null;
			}
			queue = [];
			queuedCredentials = /* @__PURE__ */ new WeakMap();
			attempts = /* @__PURE__ */ new WeakMap();
			fingerprints.clear();
			generation++;
			started = false;
		},
		reportManualError: (type, message, extra = {}, stack) => {
			reportError({
				type,
				message,
				extra,
				...stack === void 0 ? {} : { stack }
			});
		},
		reportError,
		reportRequestError: (kind, message, request, extra = {}) => {
			const target = requestTarget(request);
			if (!target) {
				reportError({
					type: kind,
					message,
					extra
				});
				return;
			}
			reportError({
				type: kind,
				message: `${message} @ ${target}`,
				extra: {
					...extra,
					endpoint: target
				}
			});
		},
		markReported: (error) => {
			if (error && typeof error === "object" && Object.isExtensible(error)) error.reportedByInterceptor = true;
			return error;
		},
		captureVueError: (err, _vm, info) => {
			const e = err;
			reportError({
				type: "vue_error",
				message: (e === null || e === void 0 ? void 0 : e.message) || String(err) || "Vue error",
				stack: (e === null || e === void 0 ? void 0 : e.stack) || "No stack trace",
				extra: { componentInfo: info || "" }
			});
		}
	};
}
//#endregion
export { createReporter, errorFingerprint, requestTarget, sanitizeRequestData };
